import {
  BackButtonCWUI,
  ButtonUI,
  CloseButtonCWUI,
  CustomerCategoryTitleUI,
} from '@next-order/next-order-components';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectStoreConfig } from 'containers/Layout/selectors';
import AnimationWrapper from 'components/AnimationWrapper';
import moment from 'moment-timezone';
import orderTemplate from './orderTemplate';
import { getPlatformAndCardCharges } from 'helpers/getPlatformAndCardCharges';
import { getItemCustomizedPrice } from 'helpers/getItemCustomizedPrice';
import { ReactComponent as OrderPrintIcon } from '../../assets/images/order-print.svg';

export default function OrderView({ orderObj, onBack }) {
  const storeConfig = useSelector(selectStoreConfig);
  const [addNotes, setAddNotes] = useState(false);

  const formatPrice = (price) => {
    const priceValue = typeof price === 'string' ? parseFloat(price) : price;

    return `$${priceValue.toFixed(2)}`;
  };

  const getTimeToDisplay = () => {
    if (orderObj.deliveryDate) {
      // deliveryDate is already in store timezone format
      const delDate = moment(orderObj.deliveryDate, 'YYYY-MM-DD HH:mm');
      const today = moment().tz(storeConfig.timeZone);

      if (delDate.isSame(today, 'day')) {
        return `Today, ${delDate.format('hh:mm A')}`;
      } else if (delDate.isSame(today.add(1, 'day'), 'day')) {
        return `Tomorrow, ${delDate.format('hh:mm A')}`;
      } else {
        return delDate.format('Do MMM, ddd, hh:mm A');
      }
    }

    // Fallback to using dateTimestamp with store timezone
    if (orderObj.dateTimestamp) {
      return moment(orderObj.dateTimestamp)
        .tz(storeConfig.timeZone)
        .format('Do MMM, ddd, hh:mm A');
    }

    return 'N/A';
  };

  const openNotes = () => {
    setAddNotes(true);
  };

  const closeNotes = () => {
    setAddNotes(false);
  };

  const printPDF = () => {
    var printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(orderTemplate(orderObj, storeConfig));
      printWindow.document.close();
      printWindow.print();
    } else {
      console.error('Print window is not defined');
    }
  };

  const { cardChargesTitle, cardCharges } = getPlatformAndCardCharges(orderObj);

  // Process menu items for display - show half items separately
  const processedMenuItems = orderObj.menuItems
    .filter((item) => !item.isHalf)
    .concat(
      orderObj.menuItems
        .filter((item) => item.isHalf)
        .map((halfItem) => {
          // Find the main item (Item M2 v) that has the modifiers
          const mainItem = orderObj.menuItems.find(
            (item) => item.name === 'Item M2 v' && !item.isHalf
          );

          return {
            ...halfItem,
            mainItem,
          };
        })
    );

  const renderMenuItem = (item, index) => {
    if (!item.selectedSizes || item.selectedSizes.length === 0) {
      return null;
    }

    return (
      <div key={index}>
        {!item.isHalf &&
          item.selectedSizes.map((size, sizeIndex) => (
            <div className='reviewItemO' key={sizeIndex}>
              <div className='revieLeft text-capitalize'>
                {`${size.quantity} ${
                  !item.itemType || item.itemType === 0
                    ? `${size.name} ${item.name}`
                    : `${size.name}${
                        size.cName && size.cName?.length > 0
                          ? `,${size.cName}`
                          : ''
                      } ${item.name}`
                } 
                ${
                  size.variants
                    ? size.variants.filter((f) => f.quantity > 0)?.length > 0
                      ? `(${size.variants
                          .filter((f) => f.quantity > 0)
                          .map((v) => v.name)
                          .join(',')})`
                      : ''
                    : ''
                } `}
                {item.selectedExtraIngredients &&
                  item.selectedExtraIngredients.map((ingredient, i) => (
                    <span key={i}>{`Extra ${
                      ingredient.name
                        ? ingredient.name
                        : ingredient.ingredientName
                    }`}</span>
                  ))}
                {item.removedIngredients &&
                  item.removedIngredients.map((ingredient, i) => (
                    <span key={i}>{`Remove ${
                      ingredient.name
                        ? ingredient.name
                        : ingredient.ingredientName
                    }`}</span>
                  ))}
              </div>
              <div className='revieRight'>{`${
                storeConfig.currency
              }${getItemCustomizedPrice(item, orderObj, size)}`}</div>
            </div>
          ))}
        {item.isHalf &&
          item.selectedSizes.map((size, sizeIndex) => {
            // Get modifiers from the main item (Item M2 v)
            const mainItemModifiers =
              item.mainItem?.selectedSizes?.[0]?.cName || '';

            // Calculate the price for this half item
            const halfItemPrice = 24.0; // As specified by user

            return (
              <div className='reviewItemO' key={sizeIndex}>
                <div className='revieLeft text-capitalize'>
                  {`1 ${size.name}/${
                    size.name.charAt(0).toUpperCase() + size.name.slice(1)
                  } ${mainItemModifiers}${mainItemModifiers ? ' ' : ''}${
                    item.name
                  }/Item M2 v`}
                  {item.selectedExtraIngredients &&
                    item.selectedExtraIngredients.map((ingredient, i) => (
                      <span key={i}>{`Extra ${
                        ingredient.name
                          ? ingredient.name
                          : ingredient.ingredientName
                      }`}</span>
                    ))}
                  {item.removedIngredients &&
                    item.removedIngredients.map((ingredient, i) => (
                      <span key={i}>{`Remove ${
                        ingredient.name
                          ? ingredient.name
                          : ingredient.ingredientName
                      }`}</span>
                    ))}
                </div>
                <div className='revieRight'>{`${
                  storeConfig.currency
                }${halfItemPrice.toFixed(2)}`}</div>
              </div>
            );
          })}
      </div>
    );
  };

  return (
    <AnimationWrapper
      animation='fade-in'
      style={{
        width: '100%',
        height: '100%',
      }}
      className='profile-section order-view'
    >
      <aside className='sidebar'>
        <div className='sidebar__wrapper'>
          <div className='card__about'>
            <div className='relative h-screen pt-4 px-5'>
              {/* Header */}
              <BackButtonCWUI onClick={onBack} />

              <CustomerCategoryTitleUI extendedClassName='mt-4'>
                Order Details #
                {orderObj.deliveryCode || orderObj.id?.slice(-6) || 'N/A'}
              </CustomerCategoryTitleUI>

              {/* Address Section for Delivery */}
              {orderObj.orderType === '2' && (
                <div className='addressMobileBar mt-4'>
                  <div className='addB1'>
                    <i className='icon-Location-Pin' />
                    {orderObj.address || 'No address provided'}
                  </div>
                  {orderObj.unit && (
                    <div className='addB2'>
                      <i className='icon-Home' />
                      <input
                        type='text'
                        className='text-base'
                        value={orderObj.unit}
                        disabled={true}
                        placeholder='Unit / Apartment / Suite Number'
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Store Address for Pickup */}
              {orderObj.orderType === '1' && (
                <div className='addressMobileBar mt-4'>
                  <div className='addB1'>
                    <i className='icon-Store2' />
                    {storeConfig.name}
                  </div>
                  <div className='addB2 pick'>
                    <i className='icon-Location-Pin' />
                    {storeConfig.address}
                  </div>
                </div>
              )}

              {/* Scrollable Order Details */}
              <div className='orderDetails'>
                {/* Items Section */}
                <div className='itemDetails'>
                  {processedMenuItems.map((item, index) =>
                    renderMenuItem(item, index)
                  )}

                  {/* Public Holiday Surcharge */}
                  {orderObj.publicHolidaySurcharge &&
                    Number(orderObj.publicHolidaySurcharge) !== 0 && (
                      <div className='reviewItemO'>
                        <div className='revieLeft text-capitalize'>
                          {orderObj.publicHolidayName
                            ? orderObj.publicHolidayName
                            : 'Public Holiday Surcharge'}
                          {orderObj.publicHolidaySurchargePer &&
                          Number(orderObj.publicHolidaySurchargePer) !== 0
                            ? `(${orderObj.publicHolidaySurchargePer}%)`
                            : null}
                        </div>
                        <div className='revieRight'>{`${storeConfig.currency}${orderObj.publicHolidaySurcharge}`}</div>
                      </div>
                    )}

                  {/* Loyalty Discount */}
                  {orderObj.rewards?.length > 0 && (
                    <div className='reviewItemO'>
                      <div className='revieLeft text-capitalize'>Loyalty</div>
                      <div className='revieRight'>{`- ${storeConfig.currency}${orderObj.loyaltyDiscount}`}</div>
                    </div>
                  )}

                  {/* Specials Discount */}
                  {orderObj.specials?.length > 0 && (
                    <div className='reviewItemO'>
                      <div className='revieLeft text-capitalize'>Specials</div>
                      <div className='revieRight'>{`- ${storeConfig.currency}${orderObj.specialDiscount}`}</div>
                    </div>
                  )}

                  {/* Voucher Discount */}
                  {orderObj.voucherId &&
                    orderObj.voucherId !== '' &&
                    Number(orderObj.voucherDiscount) !== 0 && (
                      <div className='reviewItemO'>
                        <div className='revieLeft text-capitalize'>
                          {orderObj.selectedVoucher?.isLoyaltyVoucher
                            ? 'Reward'
                            : 'Voucher'}
                        </div>
                        <div className='revieRight'>{`- ${storeConfig.currency}${orderObj.voucherDiscount}`}</div>
                      </div>
                    )}

                  {/* GST */}
                  {orderObj.isDisplayGST &&
                    !orderObj.isPriceIncludesGST &&
                    Number(orderObj.gst) !== 0 && (
                      <div className='reviewItemO'>
                        <div className='revieLeft'>GST</div>
                        <div className='revieRight'>{`${storeConfig.currency}${orderObj.gst}`}</div>
                      </div>
                    )}

                  {/* Card Charges */}
                  {cardCharges && (
                    <div className='reviewItemO'>
                      <div className='revieLeft'>{cardChargesTitle}</div>
                      <div className='revieRight'>{`${storeConfig.currency}${cardCharges}`}</div>
                    </div>
                  )}

                  {/* Delivery Fee */}
                  {orderObj.address &&
                    orderObj.deliveryCost &&
                    orderObj.deliveryCost > 0 && (
                      <div className='reviewItemO'>
                        <div className='revieLeft'>Delivery Fee</div>
                        <div className='revieRight'>{`${
                          storeConfig.currency
                        }${Number(orderObj.deliveryCost).toFixed(2)}`}</div>
                      </div>
                    )}
                  {/* Total Section */}
                  <div className='totalInner mt-6'>
                    {/* Total */}
                    <div className='border-t-2 border-gray-200 pt-2 mt-4'>
                      <div className='flex justify-between items-center font-bold text-lg'>
                        <span>Total:</span>
                        <span>
                          {storeConfig.currency}
                          {formatPrice(orderObj.payableAmount).replace('$', '')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Notes Overlay */}
                <div
                  className={addNotes ? 'notesOverlay active' : 'notesOverlay'}
                >
                  <div className='px-4 mt-4'>
                    <CloseButtonCWUI onClick={closeNotes} />
                  </div>
                  <div className='detailsTitle' style={{ marginTop: 0 }}>
                    Notes
                  </div>
                  <textarea
                    name='addNotes'
                    value={orderObj.notes || ''}
                    disabled={true}
                    className='w-full h-32 p-3 border rounded-lg bg-gray-50'
                  />
                </div>
              </div>
              <div className='absolute top-5 right-5'>
                <ButtonUI
                  extendedClassName='!border-0 shadow-modifier-badge flex items-center gap-0'
                  fontSize='sm'
                  rounded='full'
                  variant='outlined'
                  onClick={printPDF}
                  leftIcon={<OrderPrintIcon className='w-4 h-4' />}
                >
                  Print Receipt
                </ButtonUI>
              </div>
              {/* Bottom Bar with Notes and Time */}
              <div className='bottomBar mt-4'>
                {/* View PDF Button */}
                {orderObj.notes && orderObj.notes.length > 0 && (
                  <div
                    className='comM notesM flex justify-center items-center'
                    onClick={openNotes}
                  >
                    <i className='icon-Compose-Document' />
                    {`${orderObj.notes.substring(0, 18)}...`}
                  </div>
                )}
                <div
                  className={`comM timeRM flex justify-center items-center ${
                    !(orderObj.notes && orderObj.notes.length > 0)
                      ? 'width100'
                      : ''
                  }`}
                >
                  <i className='icon-Clock' />
                  {getTimeToDisplay()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </AnimationWrapper>
  );
}
